import { useState } from "react";
import {
  Field, FileField, ID_TYPES, Input, NG_STATES, OnboardShell, Select, Textarea, useKycSubmit,
} from "@/pages/onboarding/shared";
import { useAuth } from "@/context/AuthContext";

const STEPS = ["Personal", "Photo & ID", "Classification"];

const CLASSES = [
  { value: "INDIVIDUAL", label: "Individual" },
  { value: "BUSINESS_REPRESENTATIVE", label: "Business Representative" },
  { value: "GROUP_REPRESENTATIVE", label: "Group Representative" },
];

export default function ReferrerKyc() {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const { busy, submit } = useKycSubmit("/api/kyc/referrer");
  const [f, setF] = useState({
    full_name: user?.full_name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    address: "",
    city: "",
    state: "",
    photograph_file_id: "",
    id_type: "",
    id_number: "",
    id_document_file_id: "",
    classification: "INDIVIDUAL",
    organisation_name: "",
    relationship_declaration: "",
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const canNext = [
    f.full_name && f.phone && f.email && f.address && f.city && f.state,
    f.photograph_file_id && f.id_type && f.id_number && f.id_document_file_id,
    f.classification && f.relationship_declaration,
  ][step];

  return (
    <OnboardShell
      title="Referrer onboarding"
      subtitle="Baseline verification and a relationship declaration are required before you can refer families."
      steps={STEPS}
      step={step}
      onBack={() => setStep(step - 1)}
      onNext={() => (step === STEPS.length - 1 ? submit(f) : setStep(step + 1))}
      busy={busy}
      canNext={canNext}
      submitLabel="Submit for review"
    >
      {step === 0 && (
        <>
          <Field label="Full name"><Input value={f.full_name} onChange={(e) => set("full_name")(e.target.value)} data-testid="rf-fullname-input" /></Field>
          <Field label="Phone number"><Input value={f.phone} onChange={(e) => set("phone")(e.target.value)} data-testid="rf-phone-input" /></Field>
          <Field label="Email address"><Input type="email" value={f.email} onChange={(e) => set("email")(e.target.value)} data-testid="rf-email-input" /></Field>
          <Field label="Address"><Textarea value={f.address} onChange={(e) => set("address")(e.target.value)} data-testid="rf-address-input" /></Field>
          <Field label="City / town"><Input value={f.city} onChange={(e) => set("city")(e.target.value)} data-testid="rf-city-input" /></Field>
          <Field label="State"><Select value={f.state} onChange={set("state")} options={NG_STATES} testId="rf-state-select" /></Field>
        </>
      )}
      {step === 1 && (
        <>
          <FileField label="Photograph" value={f.photograph_file_id} onChange={set("photograph_file_id")} purpose="PHOTOGRAPH" testId="rf-photo-upload" />
          <Field label="Government ID type"><Select value={f.id_type} onChange={set("id_type")} options={ID_TYPES} testId="rf-idtype-select" /></Field>
          <Field label="ID number"><Input value={f.id_number} onChange={(e) => set("id_number")(e.target.value)} data-testid="rf-idnumber-input" /></Field>
          <FileField label="ID document photo" value={f.id_document_file_id} onChange={set("id_document_file_id")} purpose="GOVERNMENT_ID" testId="rf-iddoc-upload" />
        </>
      )}
      {step === 2 && (
        <>
          <Field label="Referrer classification"><Select value={f.classification} onChange={set("classification")} options={CLASSES} testId="rf-classification-select" /></Field>
          {f.classification !== "INDIVIDUAL" && (
            <Field label="Organisation / group name"><Input value={f.organisation_name} onChange={(e) => set("organisation_name")(e.target.value)} data-testid="rf-org-input" /></Field>
          )}
          <Field label="Relationship declaration">
            <Textarea
              value={f.relationship_declaration}
              onChange={(e) => set("relationship_declaration")(e.target.value)}
              placeholder="Describe your relationship to the people you intend to refer."
              data-testid="rf-relationship-input"
            />
          </Field>
        </>
      )}
    </OnboardShell>
  );
}
